const DOCUMENT_TYPE_FIELD_HINTS = {
  ine: ["ine", "identificacion", "id", "votar"],
  curp: ["curp"],
  acta_nacimiento: ["acta", "nacimiento"],
  estado_cuenta: ["estado", "cuenta", "banc"],
  comprobante_domicilio: ["domicilio", "cfe", "agua", "predial"],
  rfc_constancia: ["rfc", "fiscal", "constancia"],
  nomina: ["nomina", "ingreso", "recibo"],
  otro: [],
};

const DATA_FIELD_HINTS = {
  curp: ["curp"],
  rfc: ["rfc"],
  email: ["correo", "email", "e-mail"],
  telefono: ["telefono", "celular", "movil"],
  fechaNacimiento: ["fecha_nacimiento", "nacimiento", "fecha"],
  nombres: ["nombre", "nombres", "cliente"],
  ingresosMensuales: ["ingreso", "sueldo", "mensual", "monto", "valor"],
};

function normalize(value = "") {
  return String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();
}

function isDocumentFieldType(type = "") {
  const value = normalize(type);
  return (
    value.includes("doc") ||
    value.includes("pdf") ||
    value.includes("imagen") ||
    value.includes("archivo") ||
    value.includes("file")
  );
}

function flattenProcesses(processes = [], level = 0, parentPath = []) {
  const out = [];
  (processes || []).forEach((process, processIndex) => {
    const currentPath = [...parentPath, process?.vc_nombre || process?.id || `proceso_${processIndex}`];
    const fields = Array.isArray(process?.fields) ? process.fields : [];
    fields.forEach((field, fieldIndex) => {
      out.push({
        ...field,
        _process_id: process?.id || null,
        _process_name: process?.vc_nombre || "",
        _process_path: currentPath,
        _level: level,
        _field_index: fieldIndex,
      });
    });
    const children = Array.isArray(process?.sub_processes)
      ? process.sub_processes
      : [];
    out.push(...flattenProcesses(children, level + 1, currentPath));
  });
  return out;
}

function getCurrentValue(field, currentValues = {}) {
  const live = currentValues?.[field?.id];
  if (live !== undefined && live !== null && `${live}`.trim() !== "") return live;

  const value = field?.value || {};
  if (value?.id_documento) return value.id_documento;
  if (value?.vc_valor !== undefined && value?.vc_valor !== null && `${value?.vc_valor}`.trim() !== "") {
    return value.vc_valor;
  }
  if (value?.nu_valor !== undefined && value?.nu_valor !== null && `${value?.nu_valor}`.trim() !== "") {
    return value.nu_valor;
  }
  if (value?.tx_valor !== undefined && value?.tx_valor !== null && `${value?.tx_valor}`.trim() !== "") {
    return value.tx_valor;
  }
  return "";
}

function fieldText(field) {
  return normalize([
    field?.vc_clave,
    field?.vc_nombre,
    field?.vc_label,
    field?._process_name,
    ...(field?._process_path || []),
  ].filter(Boolean).join(" "));
}

function rankDocumentField(field, docType, currentValues = {}) {
  const text = fieldText(field);
  const hints = DOCUMENT_TYPE_FIELD_HINTS[docType] || [];
  let score = 0;

  if (field?.bl_requerido) score += 40;
  if (!getCurrentValue(field, currentValues)) score += 30;
  if (field?._level === 0) score += 5;
  score += Math.max(0, 20 - (Number(field?.nu_orden) || 99));

  hints.forEach((hint) => {
    if (text.includes(hint)) score += 20;
  });

  return score;
}

function rankDataField(field, semanticKey, currentValues = {}) {
  const text = fieldText(field);
  const hints = DATA_FIELD_HINTS[semanticKey] || [];
  let score = 0;

  if (field?.bl_requerido) score += 40;
  if (!getCurrentValue(field, currentValues)) score += 30;
  score += Math.max(0, 20 - (Number(field?.nu_orden) || 99));

  hints.forEach((hint) => {
    if (text.includes(hint)) score += 20;
  });

  const type = normalize(field?.vc_tipo || "");
  if (semanticKey === "email" && type.includes("email")) score += 15;
  if (semanticKey === "telefono" && type.includes("telefon")) score += 15;
  if (semanticKey === "fechaNacimiento" && type.includes("fecha")) score += 15;
  if (semanticKey === "ingresosMensuales" && (type.includes("numer") || type.includes("moneda") || type.includes("money"))) {
    score += 15;
  }

  return score;
}

export function getSchemaFields(schema = {}) {
  const processes = Array.isArray(schema?.processes) ? schema.processes : [];
  return flattenProcesses(processes);
}

export function findCompatibleDocumentFields(schema = {}, docType = "otro", currentValues = {}) {
  const fields = getSchemaFields(schema).filter((field) => isDocumentFieldType(field?.vc_tipo));
  return fields
    .map((field) => ({
      ...field,
      _score: rankDocumentField(field, docType, currentValues),
    }))
    .sort((a, b) => b._score - a._score);
}

export function mapExtractedDataToFields(schema = {}, extracted = {}, currentValues = {}) {
  const fields = getSchemaFields(schema).filter((field) => !isDocumentFieldType(field?.vc_tipo));
  const mapped = [];

  Object.entries(extracted || {}).forEach(([key, value]) => {
    if (
      ["raw_excerpt"].includes(key) ||
      value === null ||
      value === undefined ||
      `${value}`.trim() === ""
    ) {
      return;
    }

    const ranked = fields
      .map((field) => ({
        field,
        score: rankDataField(field, key, currentValues),
      }))
      .filter((candidate) => candidate.score > 0)
      .sort((a, b) => b.score - a.score);

    const best = ranked[0];
    if (!best?.field?.id) return;

    mapped.push({
      semanticKey: key,
      id_field: best.field.id,
      id_proceso: best.field._process_id || best.field.id_proceso || null,
      field: best.field,
      value,
      score: best.score,
    });
  });

  const dedupByField = new Map();
  mapped.forEach((item) => {
    const existing = dedupByField.get(item.id_field);
    if (!existing || item.score > existing.score) {
      dedupByField.set(item.id_field, item);
    }
  });

  return Array.from(dedupByField.values());
}

export function suggestAssignment(schema = {}, docType = "otro", extracted = {}, currentValues = {}) {
  const documentCandidates = findCompatibleDocumentFields(
    schema,
    docType,
    currentValues,
  );
  const suggestedDocumentField = documentCandidates[0] || null;
  const dataMappings = mapExtractedDataToFields(schema, extracted, currentValues);

  return {
    suggestedDocumentField,
    documentCandidates,
    dataMappings,
  };
}
